import { GetServerSideProps, NextPage } from 'next';
import getConfig from 'next/config';
import { useRouter } from 'next/router';

const { publicRuntimeConfig } = getConfig();

import { LISTING_ROWS_PER_PAGE } from '@klokhuis/constants/Listings';
import { AnalyticsPageCategory } from '@klokhuis/core/Analytics/types';
import { getListingAnalyticsPageName } from '@klokhuis/core/Analytics/utilities/getListingAnalyticsPageName';
import {
  FindListingsResponse,
  ListingRequest,
  Location,
  PropertyTypeID,
} from '@klokhuis/core/ApiClient/Klokhuis';
import { logger } from '@klokhuis/core/Logger';
import { PageLayout } from '@klokhuis/core/PageLayout';
import { ROUTES } from '@klokhuis/core/Routing';

import { mockFindListingsResponse } from '../../__fixtures__/ListingsMocks';
import { ListingsContext } from '../../context/ListingsContext';
import { ListingsProvider } from '../../context/ListingsProvider';
import { useListingSelectors } from '../../context/hooks/useListingSelectors';
import { getPropertyTypeAndValueFromRoute } from '../../utilities/getListingRouteValues';
import { getListingTypesFromUrl } from '../../utilities/getListingTypesFromUrl';

import Search from './components/Search/Search';
import SearchViews from './components/SearchView';
import {
  getLocationBySlug,
  getSellersBySlug,
  getSubdomainFromHost,
} from './helpers/Util';
import useMetaTags from './hooks';

type ListingSearchCityRouteParams = Parameters<
  typeof ROUTES.ListingSearchCity.url
>[0];

interface ListingsProps {
  searchCriteria: ListingRequest | null;
  listings: FindListingsResponse | null;
  listingTypes: PropertyTypeID[] | null;
  favoriteIds?: number[];
}

export const getServerSideProps: GetServerSideProps<
  ListingsProps
> = async ctx => {
  try {
    // Use default params for home route
    const typeAndCity =
      (ctx.params?.typeAndCity as string) || 'properties-india';
    const { propertyType, value: citySlug } =
      getPropertyTypeAndValueFromRoute(typeAndCity);
    const subDomain = getSubdomainFromHost(ctx);
    const { citySlugId, cityName } = await getLocationBySlug(ctx, citySlug);
    const listingTypes = await getListingTypesFromUrl(propertyType, ctx);

    // Extract page number from query parameters
    const page = parseInt(ctx.query.page as string) || 1;

    if (!listingTypes) {
      return {
        props: {
          searchCriteria: null,
          listings: null,
          listingTypes: [],
        },
      };
    }

    // fixme: using mock data for now
    // const listings = mockFindListingsResponse;

    let listings: FindListingsResponse | null = null;
    let sellerDisplayName: string | null = null;
    try {
      if (subDomain) {
        const { sellerId, displayName } = await getSellersBySlug(
          ctx,
          subDomain,
        );
        logger.info(`Fetching listings for subdomain: ${subDomain}`);
        listings = await ctx.req.apiClient.listing.findListingsBySellerId({
          sellerId: sellerId?.toString() || '0',
          page,
        });
        sellerDisplayName = displayName || null;
      } else if (citySlugId) {
        // If city is selected, fetch listings for that city
        listings = await ctx.req.apiClient.listing.findListingsByCity({
          cityId: citySlugId?.toString() || '0',
          page,
        });
      } else {
        // If no city is selected, fetch all available listings
        listings = await ctx.req.apiClient.listing.findAllListings({
          page,
        });
      }
    } catch (error) {
      logger.error(`Failed to fetch listings so using mock data: ${error}`);
      listings = mockFindListingsResponse;
    }

    // Best-effort: fetch favourite IDs for authenticated users
    let favoriteIds: number[] = [];
    try {
      const favRes = await ctx.req.apiClient.favorite.getFavorites();
      favoriteIds = favRes.favorites.map(f => f.listingId);
    } catch {
      // unauthenticated or network error — default to empty
    }

    return {
      props: {
        searchCriteria: {
          citySlug: cityName?.toString() || null,
          location: citySlugId
            ? ({
                id: citySlugId,
                name: cityName || null,
                cityName: citySlug, // slug used by isValidCity() and search bar display
              } as unknown as Location)
            : null,
          sellerSlug: subDomain || null,
          sellerDisplayName,
        } as unknown as ListingRequest,
        listings,
        listingTypes,
        favoriteIds,
        analytics: {
          name: getListingAnalyticsPageName([]).toLowerCase(),
          category: AnalyticsPageCategory.SearchResults,
        },
      },
    };
  } catch (ex) {
    logger.error(
      `Listing Search Page - Failed to fetch ListingSearchCriteria: ${ex}`,
    );
    logger.error(
      `Listing Search Page - Failed to fetch criteria: ${ctx.params?.typeAndCity}`,
    );
    return {
      props: {
        searchCriteria: null,
        listings: null,
        listingTypes: [],
      },
    };
  }
};

const ListingsInternal = () => {
  const router = useRouter();
  const { listingsState } = ListingsContext.useContext();
  const { filters } = listingsState.filterResult;
  const totalListings = useListingSelectors().getListingCount();

  const { metaTitle, metaDescription } = useMetaTags();

  const citySearchRouteParams = {
    typeAndCity: router.query.typeAndCity as string,
  };

  const currentPage = filters.pageNo || 1;
  const totalPages = Math.ceil(
    (totalListings || 0) / (filters.rowsPerPage || 10),
  );
  const baseUrl =
    publicRuntimeConfig.ORIGIN +
    ROUTES.ListingSearchCity.url(citySearchRouteParams);

  // JSON-LD: BreadcrumbList for listings page
  const cityName = filters.location?.cityName ?? 'India';
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: publicRuntimeConfig.ORIGIN + '/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: metaTitle,
        item: baseUrl,
      },
    ],
  };

  // JSON-LD: SearchResultsPage
  const searchResultsJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SearchResultsPage',
    name: metaTitle,
    description: metaDescription,
    url: baseUrl,
    about: {
      '@type': 'Place',
      name: cityName,
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'IN',
        addressLocality: cityName,
      },
    },
  };

  // First listing's thumbnail for og:image
  const firstListing = listingsState.listingsResult.listings
    ?.listings?.[0] as any;
  const ogImage = firstListing?.thumbnailUrl ?? undefined;

  return (
    <PageLayout
      pageTitle={metaTitle}
      metaDescription={metaDescription}
      route={ROUTES.ListingSearchCity}
      routeParams={{
        ...citySearchRouteParams,
        typeAndCity: citySearchRouteParams.typeAndCity,
      }}
      ogImage={ogImage}
      pagination={
        totalPages > 1 ? { page: currentPage, totalPages, baseUrl } : undefined
      }
      jsonLd={[breadcrumbJsonLd, searchResultsJsonLd]}
      hideFooter={false}
      isBigHeader
    >
      {!filters.sellerSlug && <Search />}
      <SearchViews />
    </PageLayout>
  );
};

const Listings: NextPage<ListingsProps> = ({
  searchCriteria,
  listings,
  listingTypes,
  favoriteIds,
}) => {
  return (
    <ListingsProvider
      initialListings={listings}
      initialFilters={{
        ...(searchCriteria as ListingRequest),
        rowsPerPage: LISTING_ROWS_PER_PAGE,
        listingTypeIds: listingTypes,
      }}
      initialFavoriteIds={favoriteIds}
    >
      <ListingsInternal />
    </ListingsProvider>
  );
};

export default Listings;
